import {ApiClient} from '@hconnect/apiclient'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import qs from 'qs'
import {useEffect} from 'react'
import {useNavigate} from 'react-router'

import {TasksApiPath} from '../../consts'
import {mapTasksFiltersParams, mapTasksQueryParams} from '../../mappers'
import {PaginationResponse, HProduceTask, UseTasksParams} from '../../types'
import {handleTasksError as handleError} from '../../utils'

export const TasksQueryKey = 'tasks'

export const useTasks = (
  params: UseTasksParams,
  options?: Omit<
    UseQueryOptions<PaginationResponse<HProduceTask>, AxiosError, PaginationResponse<HProduceTask>>,
    'queryKey' | 'queryFn'
  >
) => {
  const navigate = useNavigate()
  const {limit, pageNumber, sources, assignedToMe, createdByMe, filters, sorting, timezone, areaPlantIds} = params
  const offset = pageNumber * limit

  const query = useQuery<
    PaginationResponse<HProduceTask>,
    AxiosError,
    PaginationResponse<HProduceTask>
  >({
    queryKey: [
      TasksQueryKey,
      limit,
      offset,
      sources,
      assignedToMe,
      createdByMe,
      filters,
      sorting,
      timezone,
      areaPlantIds
    ],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.post<PaginationResponse<HProduceTask>>(
        TasksApiPath.TASKS,
        mapTasksFiltersParams(timezone, filters, areaPlantIds),
        {
          params: mapTasksQueryParams(params, offset),
          paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat'}),
          signal
        }
      )

      return response.data ?? []
    },
    refetchOnWindowFocus: 'always',
    ...options
  })

  useEffect(() => {
    if (query.error) {
      handleError(query.error, navigate)
    }
  }, [query.error, navigate])

  return query
}
